import Col from "antd/es/col";
import Row from "antd/es/row";
import styled from "styled-components";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import { PieChartDataPoint } from "@shared/types";
import Flex from "common/Boxes/Flex";
import { useWindowSize } from "react-use";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

interface PieChartExtraInfoGridProps {
  data: PieChartDataPoint[];
  numGridItems?: number;
  numItemsInRow?: number;
  numRows?: number;
  horizontal?: boolean;
  gap?: [number, number];
  isMobileMainPageChart?: boolean;
  isTwoLinesEllipsis?: boolean;
}

const PieChartExtraInfoGrid = ({
  data,
  numGridItems = 6,
  numItemsInRow,
  numRows: propsNumRows,
  horizontal,
  gap,
  isMobileMainPageChart,
  isTwoLinesEllipsis,
}: PieChartExtraInfoGridProps) => {
  const windowSize = useWindowSize();
  const numRows = isMobileMainPageChart ? 3 : propsNumRows || 2;

  const getNumItemsInRow = () => {
    if (isMobileMainPageChart) return 1;
    const numInRow = numItemsInRow || numGridItems / numRows;
    return data.length < numInRow ? data.length : numInRow;
  };
  const numInRow = getNumItemsInRow();

  return (
    <Flex
      style={
        isMobileMainPageChart
          ? { flex: `1 1 ${windowSize.width / 2.5}px` }
          : undefined
      }
    >
      {isMobileMainPageChart && <Divider />}
      <ExtraInfoWrapper $horizontal={horizontal}>
        <Row
          gutter={gap?.length ? gap : [12, 8]}
          style={{
            justifyContent: data.length <= 2 ? "space-between" : "unset",
          }}
        >
          {data.slice(0, numRows * numInRow).map((dataPoint, index) => (
            <NameAndColor
              key={index}
              name={dataPoint.legendDisplay ?? dataPoint.name}
              item={dataPoint}
              numInRow={numInRow}
              horizontal={horizontal}
              isMobileMainPageChart={isMobileMainPageChart}
              isTwoLinesEllipsis={isTwoLinesEllipsis}
            />
          ))}
        </Row>
      </ExtraInfoWrapper>
    </Flex>
  );
};

export default PieChartExtraInfoGrid;

interface NameAndColorProps {
  item: PieChartDataPoint;
  name?: string | React.JSX.Element;
  numInRow: number;
  horizontal?: boolean;
  isMobileMainPageChart?: boolean;
  isTwoLinesEllipsis?: boolean;
}

const NameAndColor = ({
  item,
  name,
  numInRow,
  horizontal,
  isMobileMainPageChart,
  isTwoLinesEllipsis,
}: NameAndColorProps) => {
  const fontSize = isMobileMainPageChart
    ? typography.fontSizes.M
    : typography.fontSizes.S;

  return (
    <Wrapper
      span={24 / numInRow}
      $horizontal={horizontal || isMobileMainPageChart}
    >
      <Color $bg={item.color} />
      <div
        style={{
          width: isMobileMainPageChart ? 100 : "100%",
          overflow: "hidden",
        }}
      >
        {typeof name === "string" ? (
          <StyledText
            $weight={typography.fontWeights.NORMAL}
            $size={fontSize}
            $ellipsis={!isTwoLinesEllipsis}
            $isTwoLinesEllipsis={isTwoLinesEllipsis}
          >
            {name}
          </StyledText>
        ) : (
          name
        )}
        {isMobileMainPageChart && (
          <LegendSecondaryText $size={fontSize}>
            {item.value}%
          </LegendSecondaryText>
        )}
      </div>
    </Wrapper>
  );
};

const Divider = styled.div`
  height: 150px;
  width: 1px;
  background-color: ${(p) => p.theme.colors.hardBorderGrey};
  margin: 0 32px 0 16px;
`;
const StyledText = styled(Text)<{ $isTwoLinesEllipsis?: boolean }>`
  width: 100%;
  ${(p) =>
    p.$isTwoLinesEllipsis &&
    `
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
  `};
`;
const LegendSecondaryText = styled(Text)`
  margin-top: 2px;
  opacity: 0.4;
`;
const ExtraInfoWrapper = styled.div<{
  $horizontal?: boolean;
}>`
  position: relative;
  border-radius: 12px;
  padding: ${(p) => (p.$horizontal ? "0 5px" : "5px 0")};
  width: 100%;
`;
const Wrapper = styled(Col)<{ $horizontal?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: ${(p) => (p.$horizontal ? "flex-start" : "center")};
  padding: 4px 6px;
`;
const Color = styled.div<{ $bg: string }>`
  background: ${(p) => p.$bg};
  height: 8px;
  width: 8px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
  @media (max-width: ${phoneMaxWidth}) {
    margin-right: 12px;
  }
`;
